import React, { useEffect, useState } from 'react';
import { AppColors } from '../../Utils/helper/AppColors';
import {
	Box,
	Card,
	Divider,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { CallIcon, CloseIcon, CopyIcon, LocationPinIcon, MailIcon, UserIcon } from '../Icons/Icons';
import { copyToClipboard, getColorSettings, getInitials } from '../../Utils/helper/commonUtils';
import CultService from '../../Services/CultService';
import CircularProgress from '../../Screens/Dashboard/Components/CircularProgress';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import DotChip from '../DotChip';
import CallRecordingsList from '../../Screens/Recordings/Components/CallRecordingsList';
import CustomTag from '../CustomTag';
import DataNotFound from '../DataNotFound';
import { theme } from 'antd';

const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	bgcolor: AppColors.WHITE,
	borderRadius: 3,
	width: '1065px',
	maxHeight: '90vh', // Ensures the modal doesn't exceed the viewport height
	// overflow: 'auto', // Enables scrolling
};

	const getCircularProgressColor = (value) => {
		if (value < 30) return ['#F54A23' || AppColors.ERROR_DARKER, '#FFB8A1' || AppColors.ERROR_LIGHTER]; // Red for values less than 30
		if (value >= 30 && value < 70) return ['#FFC94E' || AppColors.WARNING_DARKER, '#FFE7A6' || AppColors.WARNING_LIGHTER]; // Yellow for values between 30 and 70
		return ['#2BC17F' || AppColors.SUCCESS_DARKER, '#A8F3D0' || AppColors.SUCCESS_LIGHTER]; // Green for values greater than 70
	};

export default function NewUserDetails({ userId, onClose, type, noPagination }) {
	const [details, setDetails] = useState({});
	const [issues, setIssues] = useState([]);
	const [recordings, setRecordings] = useState([]);
	const [pagination, setPagination] = useState({ page: 1, rowsPerPage: 10, count: 0 });

	const company = useSelector((state) => state?.app?.company);

	const getUserDetails = async () => {
		let res = await CultService.getUserById({ params: { id: userId } });
		if (res.status === 200) {
			console.log('User Details:', res?.data?.data);
			setDetails(res?.data?.data);
		}
	};

	const getIssues = async () => {
		try {
			let res = await CultService.getCallIssuesByUser({
				params: {
					companyId: company?._id,
					userId: userId,
				},
			});
			// console.log('Issues:', res?.data?.data?.issues);
			setIssues(res?.data?.data?.issues);
		} catch (err) {
			toast.error('Something went wrong!');
		}
	};

	const getRecordings = async (page = 1, limit = pagination?.rowsPerPage) => {
		try {
			let res = await CultService.getUserCallRecording({
				params: {
					company: company?._id,
					userId: userId,
					...(noPagination ? {} : { page: pagination.page, limit: pagination.rowsPerPage }),
				},
			});
			setRecordings(res?.data?.data?.recordings);
			setPagination({
				page: res?.data?.data?.currentPage,
				count: res?.data?.data?.totalRecords,
				rowsPerPage: limit,
				totalPages: res?.data?.data?.totalPages,
			});
		} catch (err) {
			toast.error('Something went wrong!');
		}
	};

	useEffect(() => {
		if (company) {
			getIssues();
			getUserDetails();
		}
	}, [company]);

	useEffect(() => {
		if (!company) return;
		getRecordings();
	}, []);

	    const localSettingColors = getColorSettings();


   return (
      
		<Stack sx={style}>
			{/* Header Section */}
			<Stack
				px={3}
				py={2}
				position="relative"
				gap={2}
				sx={theme => ({
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: { xs: 'flex-start', md: 'center' },
					justifyContent: 'space-between',
					borderRadius: '15px 15px 0 0',
					backgroundColor: AppColors?.lightest_grey || theme.palette.primary.lightest_shade,
				})}
			>
				{/* Left: Avatar and User Info */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						alignItems: { xs: 'flex-start', sm: 'center' },
						gap: 2,
						width: '100%',
					}}
				>
					{/* Avatar */}
					<Box
						sx={{
							height: 80,
							width: 80,
							borderRadius: '50%',
							bgcolor: AppColors.lightest_grey || AppColors.BORDER_GREY,
							border: '1px solid #dee1e3',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<UserIcon size={50} color={AppColors?.midnight_black || localSettingColors.fontColor} />
					</Box>

					{/* User Details */}
					<Box sx={{ color: localSettingColors.fontColor, width: '100%' }}>
						<Typography
							sx={theme => ({ fontWeight: 600, fontSize: 24, lineHeight: '130%',  color: theme.palette.primary.darker_primary  , mb: 1 })}
						>
							{details?.anonymous ? 'Anonymous' : `${details?.firstName || ''} ${details?.lastName || ''}`}
						</Typography>

						<Stack
							direction="row"
							flexWrap="wrap"
							gap={1} // smaller gap for less spacing
							sx={{ width: '100%' }}
						>
							{/* Left: Role & Email */}
							<Stack flex="1 1 220px" minWidth={180} gap={0.5}>
								<Stack direction="row" alignItems="center" gap={1}>
									<UserIcon size={16} color={localSettingColors.fontColor} />
									<Typography
										sx={(theme) => ({
											fontWeight: 600,
											fontSize: 14,
											lineHeight: '150%',
											
											color: theme.palette.primary.darker_primary,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '100%',
										})}
									>
										{details?.role}
									</Typography>
								</Stack>
								<Stack direction="row" alignItems="center">
									<MailIcon size={16} color={localSettingColors.fontColor} />
									<Typography
										sx={ theme => ({
											fontWeight: 600,
											fontSize: 14,
											lineHeight: '150%',
											
											color: theme.palette.primary.darker_primary,
											ml: 1,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '100%',
										})}
									>
										{details?.email}
									</Typography>
									{details?.email && details?.email !== 'N/A' && (
										<IconButton onClick={() => copyToClipboard(details?.email)} size="small">
											<CopyIcon size={16} />
										</IconButton>
									)}
								</Stack>
							</Stack>

							{/* Right: Country & Phone */}
							<Stack flex="1 1 220px" minWidth={180} gap={0.5}>
								<Stack direction="row" alignItems="center" gap={1}>
									<LocationPinIcon size={16} color={localSettingColors.fontColor} />
									<Typography
										sx={ theme => ({
											fontWeight: 600,
											fontSize: 14,
											lineHeight: '150%',
											color: theme.palette.primary.darker_primary,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '100%',
										})}
									>
										{details?.country}
									</Typography>
								</Stack>
								<Stack direction="row" alignItems="center">
									<CallIcon size={16} color={localSettingColors.fontColor} />
									<Typography
										sx={theme => ({
											fontWeight: 600,
											fontSize: 14,
											lineHeight: '150%',
											color: theme.palette.primary.darker_primary,
											ml: 1,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											maxWidth: '100%',
										})}
									>
										{details?.phoneNumber}
									</Typography>
									{details?.phoneNumber && details?.phoneNumber !== 'N/A' && (
										<IconButton onClick={() => copyToClipboard(details?.phoneNumber)} size="small">
											<CopyIcon size={16} />
										</IconButton>
									)}
								</Stack>
							</Stack>
						</Stack>
					</Box>
				</Box>

				{/* Participation Rate Card */}
				<Card
					noShadow
					sx={theme => ({
						width: { xs: '100%', sm: 180 },
						mt: { xs: 2, md: 0 },
						p: 2,
						backgroundColor: AppColors?.lightest_grey || theme.palette.primary.lightest_shade,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					})}
				>
					<Stack gap={1} alignItems="center" justifyContent="center">
						<CircularProgress hideBorders size={80} value={parseInt(+details?.participationRate || 0)} color={getCircularProgressColor(parseInt(+details?.participationRate || 0))} />
						<Typography
							sx={{
								fontWeight: 600,
								fontSize: 14,
								lineHeight: '150%',
								color: AppColors.BLACK,
								textWrap: 'nowrap',
							}}
						>
							Participation Rate
						</Typography>
					</Stack>
				</Card>

				{/* Close Button */}
				<IconButton
					onClick={onClose}
					size="small"
					sx={{
						position: 'absolute',
						top: 8,
						right: 8,
						display: { xs: 'none', sm: 'inline-flex' },
					}}
				>
					<CloseIcon />
				</IconButton>
			</Stack>

			<Divider />

            <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 200px)' }}>

			{/* Issues Section */}
			<Stack gap={1} p={3}>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: 20,
						lineHeight: '130%',
						color: AppColors.BLACK,
					}}
				>
					Issues
				</Typography>
				<TableContainer sx={{ minHeight: 'fit-content', borderRadius: '10px' }}>
					{issues?.length === 0 ? (
						<DataNotFound />
					) : (
						<Table stickyHeader>
							<TableHead
								sx={{
									'& .MuiTableCell-root': {
										fontSize: 14,
										color: AppColors.BLACK,
										fontWeight: 400,
										lineHeight: '150%',
										
										py: '8px',
									},
								}}
							>
								<TableCell>Issue</TableCell>
								<TableCell>Category</TableCell>
								<TableCell width={200}>Department</TableCell>
							</TableHead>
							<TableBody>
								{issues?.map((c, index) => (
									<TableRow key={index}>
										<TableCell>
											<Typography
												sx={{
													textTransform: 'capitalize',
													fontSize: 14,
													color: AppColors.HEADING_TITLE4,
													fontWeight: 400,
													lineHeight: '150%',
													
												}}
											>
												{c?.callIssues?.issues?.join(', ')}
											</Typography>
										</TableCell>
										<TableCell>
											{c?.callIssues?.category && (
												<CustomTag
													tagTitle={c?.callIssues?.category}
													color={AppColors.HEADING_TITLE4}
													backgroundColor={'#EEEFF0'}
													border={AppColors.HEADING_TITLE4}
												/>
											)}
										</TableCell>
										<TableCell>
											<Typography
												textTransform="capitalize"
												sx={{
													fontSize: 14,
													color: AppColors.HEADING_TITLE,
													fontWeight: 600,
													lineHeight: '150%',
													
												}}
											>
												{c?.department?.toLowerCase()}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</TableContainer>
			</Stack>

			{/* Conversations Section */}
			<Stack gap={1} p={3}>
				<Typography
					sx={{
						fontWeight: 600,
						fontSize: 20,
						lineHeight: '130%',
						
						color: AppColors.BLACK,
					}}
				>
					Conversations
				</Typography>
				<CallRecordingsList
					hideUser
					chageIconText={true}
					height="fit-content"
					fetch={getRecordings}
					recordings={recordings}
					pagination={pagination}
					noPagination={noPagination}
				/>
			</Stack>
						</Box>

		</Stack>
	);
}
